import React, { useRef } from "react";
import Select from "react-select";

import CashEntryAttachments, {
  AttachmentPreview
} from "./CashEntryAttachments";
import { CashEntryDateField } from "./CashEntryWorkflow";

const CashInEntryRow = ({
  row,
  disabled,
  loading,
  categoryOptions,
  paymentModeOptions,
  updateRow,
  onRemoveRow,
  canRemoveRow,
  attachmentsLoading = false,
  onAttachmentsChange,
  dateWindow = null
}) => {
  const inputRef = useRef(null);

  const changeAttachments = (files) => {
    if (disabled || attachmentsLoading) return;

    if (onAttachmentsChange) {
      onAttachmentsChange(files);
    } else {
      updateRow(row.id, "attachments", files);
    }
  };

  return (
    <div className="cash-in-entry-row mb-3">
      {/* Row 1: Date and Time — full width, equal columns */}
      <div className="row g-2 mb-3">
        <div className="form-group col-12">
          <CashEntryDateField
            id={`cash-in-date-${row.id}`}
            horizontal
            value={row.dateTime || ""}
            disabled={disabled}
            dateWindow={dateWindow}
            onChange={(value) =>
              updateRow(row.id, "dateTime", value)
            }
          />
        </div>
      </div>

      {/* Row 2: Category, Amount and Payment Mode */}
      <div className="row g-2 align-items-end">
        <div className="form-group col-12 col-md-4">
          <label
            htmlFor={`cash-in-category-${row.id}`}
            className="form-label"
          >
            Category
          </label>

          <Select
            inputId={`cash-in-category-${row.id}`}
            options={categoryOptions}
            value={
              categoryOptions.find(
                (option) => option.value === row.categoryId
              ) || null
            }
            onChange={(selected) =>
              updateRow(
                row.id,
                "categoryId",
                selected?.value || ""
              )
            }
            placeholder={
              loading
                ? "Loading Categories..."
                : "Select Category"
            }
            isSearchable
            isClearable
            isLoading={loading}
            isDisabled={disabled || loading}
            noOptionsMessage={() =>
              "No cash in categories found"
            }
            className="erp-select"
            classNamePrefix="erp-select"
            menuPlacement="auto"
          />
        </div>

        <div className="form-group col-12 col-md-4">
          <label
            htmlFor={`cash-in-amount-${row.id}`}
            className="form-label"
          >
            Amount (₹)
          </label>

          <input
            id={`cash-in-amount-${row.id}`}
            type="number"
            className="form-control"
            min="0"
            step="0.01"
            placeholder="Amount"
            value={row.amount}
            disabled={disabled}
            onWheel={(event) => event.currentTarget.blur()}
            onChange={(event) =>
              updateRow(row.id, "amount", event.target.value)
            }
          />
        </div>

        <div className="form-group col-12 col-md-4">
          <label
            htmlFor={`cash-in-payment-mode-${row.id}`}
            className="form-label"
          >
            Payment Mode
          </label>

          <Select
            inputId={`cash-in-payment-mode-${row.id}`}
            options={paymentModeOptions}
            value={
              paymentModeOptions.find(
                (option) => option.value === row.paymentMode
              ) || null
            }
            onChange={(selected) =>
              updateRow(
                row.id,
                "paymentMode",
                selected?.value || ""
              )
            }
            placeholder="Select Mode"
            isDisabled={disabled}
            isSearchable={false}
            className="erp-select"
            classNamePrefix="erp-select"
            menuPlacement="auto"
          />
        </div>
      </div>

      {/* Row 3: Voucher, Remark and Attachments */}
      <div className="row g-2 align-items-start mt-1">
        <div className="form-group col-12 col-md-4 col-xl-3">
          <label
            htmlFor={`cash-in-voucher-${row.id}`}
            className="form-label"
          >
            Voucher No / Transaction ID (Optional)
          </label>

          <input
            id={`cash-in-voucher-${row.id}`}
            type="text"
            className="form-control"
            placeholder="Voucher No / Transaction ID (Optional)"
            value={row.voucherNo || ""}
            disabled={disabled}
            onChange={(event) =>
              updateRow(row.id, "voucherNo", event.target.value)
            }
          />
        </div>

        <div className="form-group col-12 col-md-8 col-xl-4">
          <label
            htmlFor={`cash-in-remark-${row.id}`}
            className="form-label"
          >
            Remark
          </label>

          <input
            id={`cash-in-remark-${row.id}`}
            type="text"
            className="form-control"
            placeholder="Enter remark"
            value={row.remark || ""}
            disabled={disabled}
            onChange={(event) =>
              updateRow(row.id, "remark", event.target.value)
            }
          />
        </div>

        <div className="col-12 col-xl-5">
          <CashEntryAttachments
            inputId={`cash-in-attachments-${row.id}`}
            inputRef={inputRef}
            attachments={row.attachments || []}
            disabled={disabled}
            processing={attachmentsLoading}
            showPreviews={false}
            onChange={changeAttachments}
            onRemoveRow={onRemoveRow}
            canRemoveRow={canRemoveRow}
          />
        </div>
      </div>

      {/* Attachment previews */}
      {row.attachments?.length > 0 && (
        <div className="row g-2 mt-1">
          {row.attachments.map((file, index) => (
            <div
              key={`${file.name}-${file.lastModified}-${index}`}
              className="col-12 col-md-6"
            >
              <AttachmentPreview
                file={file}
                disabled={disabled || attachmentsLoading}
                onRemove={() =>
                  changeAttachments(
                    row.attachments.filter(
                      (_, fileIndex) => fileIndex !== index
                    )
                  )
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CashInEntryRow;